import React from 'react';

import type { Control, FieldPath } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Switch,
  Typography,
  Skeleton,
  Grid,
} from '@packages/fenrir/components';
import type { GridProps } from '@packages/fenrir/components/grid';
import { Stack } from '@packages/fenrir/components/stack';
import { useFeatureFlag } from '@packages/platform/feature-flags';

import type { ScannerDetailsForm } from '../../../../../types';

export type ScannerConfigurationProps = {
  control: Control<ScannerDetailsForm>;
  controlName: FieldPath<ScannerDetailsForm>;
  label: string;
  description: string;
  isPending: boolean;
  isDisabled?: boolean;
  isFieldSetHeader?: boolean;
  gridSize?: GridProps['size'];
};

export const ScannerConfigurationSwitch: React.FC<
  ScannerConfigurationProps
> = ({
  control,
  controlName,
  label,
  description,
  isPending,
  isDisabled,
  isFieldSetHeader = false,
  gridSize = { xs: 12, lg: 6, xl: 4 },
}) => {
  const { t } = useTranslation('telemetryScannerConfig');
  const [scannerConfigFF] = useFeatureFlag('SCANNER_CONFIGURE_ACTIVATE');
  return (
    <>
      {isFieldSetHeader && (
        <legend style={{ padding: '0px 15px' }}>{label}</legend>
      )}
      <Grid size={gridSize}>
        <Stack
          spacing={1}
          padding={scannerConfigFF ? 2 : undefined}
          gap={scannerConfigFF ? 1 : undefined}
          sx={{ justifyContent: 'space-between' }}
        >
          <Stack spacing={1}>
            {!isFieldSetHeader && (
              <Typography variant="subtitle1">{`${label}`}</Typography>
            )}
            <Typography variant="caption">{description}</Typography>
          </Stack>
          {isPending ? (
            <Skeleton
              title="scanner-config-switch-skeleton"
              width="2.5rem"
              height="1.25rem"
              variant="rounded"
            />
          ) : (
            <Controller
              control={control}
              name={controlName}
              render={({ field: { value, onChange, ...rest } }) => (
                <Stack
                  direction="row"
                  sx={{
                    transform: 'translateX(-12px)',
                  }}
                  alignItems={scannerConfigFF ? 'center' : undefined}
                >
                  <Switch
                    {...rest}
                    aria-label={label}
                    checked={value as boolean}
                    onChange={onChange}
                    disabled={isDisabled}
                    slotProps={{
                      input: {
                        'aria-label': label,
                      },
                    }}
                  />
                  {scannerConfigFF && (
                    <Typography variant="body2" color="textSecondary">
                      {`${t('labels.enable')} ${label}`}
                    </Typography>
                  )}
                </Stack>
              )}
            />
          )}
        </Stack>
      </Grid>
    </>
  );
};
